import React from 'react';

export default function DomainEventsExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Domain Events</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Qu'est-ce qu'un Domain Event ?</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Représente un fait significatif qui s'est produit dans le domaine</li>
                                <li>Est toujours au passé (PaymentProcessed, MandateValidated)</li>
                                <li>Est immutable - ne peut pas être modifié une fois créé</li>
                                <li>Contient toutes les informations pertinentes liées à l'événement</li>
                                <li>Sert de base pour la réactivité et l'historique du système</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure d'un Domain Event</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`public abstract class DomainEvent {
    private final UUID eventId;
    private final LocalDateTime occurredOn;
    private final String type;

    // Metadata
    private final String version;
    private final String source;
}

public class PaymentProcessedEvent extends DomainEvent {
    private final UUID paymentId;
    private final Amount amount;
    private final Status status;
    private final String reference;
    
    // Event-specific data
    private final ProcessingDetails details;
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples Concrets</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">PaymentProcessedEvent</h4>
                                <p className="text-sm text-gray-600">Déclenché quand un paiement est traité</p>
                                <pre className="text-xs mt-2">
{`{
    "eventId": "123e4567-e89b-12d3-a456-426614174000",
    "occurredOn": "2024-01-15T14:30:00Z",
    "type": "PAYMENT_PROCESSED",
    "paymentId": "789...",
    "amount": {"value": 100.00, "currency": "EUR"},
    "status": "COMPLETED",
    "reference": "REF123456"
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">MandateValidatedEvent</h4>
                                <p className="text-sm text-gray-600">Déclenché après validation d'un mandat</p>
                                <pre className="text-xs mt-2">
{`{
    "eventId": "123e4567-e89b-12d3-a456-426614174001",
    "occurredOn": "2024-01-15T14:31:00Z",
    "type": "MANDATE_VALIDATED",
    "mandateId": "456...",
    "creditorId": "789...",
    "validationDetails": {
        "validatedBy": "SYSTEM",
        "validationMethod": "AUTOMATIC"
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Implémentation</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Publication d'événements</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Service
public class PaymentService {
    private final DomainEventPublisher eventPublisher;

    public void processPayment(Payment payment) {
        // Process payment...
        
        // Publish event
        eventPublisher.publish(new PaymentProcessedEvent(
            payment.getId(),
            payment.getAmount(),
            PaymentStatus.COMPLETED
        ));
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Gestion d'événements</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Service
public class PaymentEventHandler {
    @EventListener
    public void handlePaymentProcessed(PaymentProcessedEvent event) {
        // Update statistics
        // Send notifications
        // Trigger workflows
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Utilisations Principales</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Audit Trail</h4>
                            <p className="text-sm text-gray-600">
                                Enregistrement de toutes les actions importantes pour l'audit et la conformité
                            </p>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Event Sourcing</h4>
                            <p className="text-sm text-gray-600">
                                Reconstruction de l'état du système à partir de la séquence d'événements
                            </p>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Notifications</h4>
                            <p className="text-sm text-gray-600">
                                Déclenchement de notifications et d'actions basées sur les événements
                            </p>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Intégration</h4>
                            <p className="text-sm text-gray-600">
                                Communication asynchrone entre différentes parties du système
                            </p>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Monitoring</h4>
                            <p className="text-sm text-gray-600">
                                Suivi en temps réel de l'état et des performances du système
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}